import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  ClipboardDocumentIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  ArrowPathIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { Message } from '@/types';
import { formatTime } from '@/utils/helpers';
import { HighlightedText } from './HighlightedText';
import { ttsService } from '@/services/ttsService';
import './MessageList.css';

interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  onSpeakingChange?: (isSpeaking: boolean) => void;
  onEditMessage?: (messageId: string, newText: string) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading,
  onSpeakingChange,
  onEditMessage,
}) => {
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editedText, setEditedText] = useState('');
  const [likedMessages, setLikedMessages] = useState<Set<string>>(new Set());
  const [dislikedMessages, setDislikedMessages] = useState<Set<string>>(new Set());

  const placeholderSuggestions = [
    'Ask me anything about technology, science, or creativity',
    'I can help you brainstorm ideas or solve problems',
    "Let's explore new concepts together",
    "What's on your mind today?",
  ];

  const handlePlayMessage = (messageId: string, text: string) => {
    if (playingMessageId === messageId) {
      // Stop if already playing
      ttsService.stop();
      setPlayingMessageId(null);
      onSpeakingChange?.(false);
    } else {
      // Play new message
      ttsService.stop();
      setPlayingMessageId(messageId);
      onSpeakingChange?.(true);
      ttsService.speak(
        text,
        {},
        () => {
          setPlayingMessageId(messageId);
          onSpeakingChange?.(true);
        },
        () => {
          setPlayingMessageId(null);
          onSpeakingChange?.(false);
        },
        () => {
          setPlayingMessageId(null);
          onSpeakingChange?.(false);
        }
      );
    }
  };

  const handleCopyMessage = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleLikeMessage = (messageId: string) => {
    setLikedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
        // Remove dislike if present
        setDislikedMessages((prevDislike) => {
          const newDislikeSet = new Set(prevDislike);
          newDislikeSet.delete(messageId);
          return newDislikeSet;
        });
      }
      return newSet;
    });
  };

  const handleDislikeMessage = (messageId: string) => {
    setDislikedMessages((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
        // Remove like if present
        setLikedMessages((prevLike) => {
          const newLikeSet = new Set(prevLike);
          newLikeSet.delete(messageId);
          return newLikeSet;
        });
      }
      return newSet;
    });
  };

  const handleRegenerateMessage = (messageId: string) => {
    // This would trigger a regeneration of the AI response
    console.log('Regenerate message:', messageId);
    // You can implement this by calling your AI service again
  };

  const startEditMessage = (messageId: string, currentText: string) => {
    setEditingMessageId(messageId);
    setEditedText(currentText);
  };

  const saveEditMessage = (messageId: string) => {
    if (editedText.trim() && onEditMessage) {
      // Call the parent handler to update the message and regenerate response
      onEditMessage(messageId, editedText.trim());
    }
    setEditingMessageId(null);
    setEditedText('');
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditedText('');
  };

  return (
    <div className="message-list">
      <div className="date-divider">Today</div>

      {messages.length === 0 && !isLoading && (
        <div className="empty-state">
          <motion.div
            className="empty-state-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="empty-state-title">Start a conversation</h3>
            <div className="suggestion-chips">
              {placeholderSuggestions.map((suggestion, index) => (
                <motion.div
                  key={index}
                  className="suggestion-chip"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  {suggestion}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      )}

      <AnimatePresence>
        {messages.map((message, index) => (
          <motion.div
            key={message.id}
            className={`message-row ${message.sender}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: 'easeOut', delay: index * 0.05 }}
          >
            {/* {message.sender === 'ai' && (
              <div className="avatar-small">
                <div className="avatar-icon"></div>
              </div>
            )} */}
            <div className={`message-wrapper ${editingMessageId === message.id ? 'editing' : ''}`}>
              <div
                className={`message-bubble ${message.sender} ${message.isError ? 'error' : ''} ${message.isTyping ? 'typing' : ''}`}
              >
                {editingMessageId === message.id ? (
                  <div className="message-edit-wrapper">
                    <textarea
                      className="message-edit-input"
                      value={editedText}
                      onChange={(e) => setEditedText(e.target.value)}
                      autoFocus
                    />
                    <div className="message-edit-actions">
                      <button
                        className="edit-action-button save"
                        onClick={() => saveEditMessage(message.id)}
                        title="Save"
                      >
                        <CheckIcon className="w-4 h-4" />
                      </button>
                      <button
                        className="edit-action-button cancel"
                        onClick={cancelEditMessage}
                        title="Cancel"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="message-content-wrapper">
                      <div className="message-text">
                        {message.sender === 'ai' && !message.isError ? (
                          <HighlightedText text={message.text} />
                        ) : (
                          message.text
                        )}
                        {message.isTyping && <span className="typing-cursor">|</span>}
                      </div>
                    </div>
                    <div className="message-time">{formatTime(message.timestamp)}</div>
                  </>
                )}
              </div>
              {!message.isError && !message.isTyping && editingMessageId !== message.id && (
                <div className="message-actions-outside">
                  <button
                    className="action-button"
                    onClick={() => handleCopyMessage(message.text)}
                    title="Copy"
                  >
                    <ClipboardDocumentIcon className="w-4 h-4" />
                  </button>
                  {message.sender === 'ai' ? (
                    <>
                      <button
                        className={`action-button ${likedMessages.has(message.id) ? 'active' : ''}`}
                        onClick={() => handleLikeMessage(message.id)}
                        title="Like"
                      >
                        <HandThumbUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        className={`action-button ${dislikedMessages.has(message.id) ? 'active' : ''}`}
                        onClick={() => handleDislikeMessage(message.id)}
                        title="Dislike"
                      >
                        <HandThumbDownIcon className="w-4 h-4" />
                      </button>
                      <button
                        className="action-button"
                        onClick={() => handleRegenerateMessage(message.id)}
                        title="Regenerate"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                      </button>
                      <button
                        className="action-button"
                        onClick={() => handlePlayMessage(message.id, message.text)}
                        title={playingMessageId === message.id ? 'Stop audio' : 'Play audio'}
                      >
                        {playingMessageId === message.id ? (
                          <SpeakerXMarkIcon className="w-4 h-4" />
                        ) : (
                          <SpeakerWaveIcon className="w-4 h-4" />
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      className="action-button"
                      onClick={() => startEditMessage(message.id, message.text)}
                      title="Edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* {message.sender === 'user' && (
              <div className="avatar-small user">
                <div className="user-icon"></div>
              </div>
            )} */}
          </motion.div>
        ))}
      </AnimatePresence>

      {isLoading && (
        <motion.div
          className="message-row ai"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="avatar-small">
            <div className="avatar-icon"></div>
          </div>
          <div className="message-bubble ai loading">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};
